import React from 'react'
import { isEmpty, omit } from 'lodash'

import { Pill } from '@/components/pills/Pill'

// Drop Five9 custom_fields rows that aren't fully configured. Both the key (the
// URL parameter name Five9 posts back) and the value (the Five9 variable
// expression) are required for a field to do anything, so a row missing either
// is dead config. No-ops for integrations without custom_fields.
export const pruneEmptyCustomFields = (values) => {
  if (!Array.isArray(values.custom_fields)) {
    return values
  }
  return {
    ...values,
    custom_fields: values.custom_fields.filter(
      (field) => !isEmpty(field?.key) && !isEmpty(field?.value)
    ),
  }
}

export const mergeOrgsandIntegrations = (integrations, organizations) => {
  return integrations.map((integration) => {
    const org = organizations.filter((it) => it.id === integration.organization_id)
    const mergedIntegration = { ...integration }
    mergedIntegration.organization_name = org.length
      ? org[0].name
      : `Organization ${integration.organization_id} missing`
    return mergedIntegration
  })
}
export const diffIntegrations = (obj1, obj2) => {
  const result = {}
  if (Object.is(obj1, obj2)) {
    return undefined
  }
  if (!obj2 || typeof obj2 !== 'object') {
    return obj2
  }
  Object.keys(obj1 || {})
    .concat(Object.keys(obj2 || {}))
    .forEach((key) => {
      if (obj2[key] !== obj1[key] && !Object.is(obj1[key], obj2[key])) {
        result[key] = obj2[key]
      }
      // Recurse only when both sides are non-null objects. `typeof null` is
      // 'object', so without the truthy guards a null-on-one-side/object-on-the-
      // other field (e.g. a DB null vs a seeded array) would recurse into
      // diffIntegrations(null, ...) and throw on obj1[key]. The value diff is
      // already captured above in that case.
      if (
        obj1[key] &&
        obj2[key] &&
        typeof obj1[key] === 'object' &&
        typeof obj2[key] === 'object'
      ) {
        const value = diffIntegrations(obj1[key], obj2[key])
        if (value !== undefined) {
          result[key] = value
        }
      }
    })
  return result
}
export const getStatusClassName = (status) => {
  if (status === 'running') {
    return { success: true }
  }
  if (status === 'uninitialized' || status === 'not_running') {
    return { emphasized: true }
  }

  return { critical: true }
}

export const formatHealthValue = (value) => {
  return value ? `${value.toString().split('.')[0]}%` : 'No Health Score'
}

export const getAndCheckOrgColumn = (org_id) => {
  if (org_id >= 90000) {
    return <Pill small>Not Assigned</Pill>
  }
  return org_id
}

/**
 * The one list of connector types.
 *
 * This fed three hand-maintained copies -- the create dropdown in
 * IntegrationsForm, the table label map below, and configTypeToSchemaMap in
 * integrations.schema.js -- which had already drifted: `api` has a display name but
 * no edit schema, and `slack` and `vonage_enterprise` have edit schemas but are not
 * creatable. Half-registering the next vendor is the failure mode this prevents.
 *
 * `createLabel` exists because Atscall deliberately reads differently in the create
 * dropdown than in the table; without it, merging the lists would silently change
 * one of them.
 */
export const CONNECTOR_TYPES = [
  { value: 'generic', label: 'Generic' },
  { value: 'ringcentral', label: 'RingCentral EX' },
  { value: 'ringcentral_engage', label: 'RingCentral CX' },
  { value: 'genesys', label: 'Genesys Cloud' },
  { value: 'five9', label: 'Five9' },
  { value: 'five9_ess', label: 'Five9 ESS' },
  { value: '8x8', label: '8x8 VCC' },
  { value: '8x8_v2', label: '8x8 VCC V2' },
  { value: 'amazon_connect', label: 'Amazon Connect' },
  { value: 'zoom', label: 'Zoom' },
  { value: 'zoom_video', label: 'Zoom Video' },
  { value: 'zoom_contact_center', label: 'Zoom Contact Center' },
  { value: 'close', label: 'Close' },
  { value: 'aircall', label: 'AirCall' },
  { value: 'tld', label: 'TLD' },
  { value: 'trackdrive', label: 'Trackdrive' },
  { value: 'sinch', label: 'Sinch' },
  { value: 'thrio', label: 'Thrio' },
  { value: 'nice', label: 'Nice' },
  { value: 'tcn', label: 'TCN' },
  { value: 'api', label: 'API' },
  { value: 'regal', label: 'Regal' },
  { value: 'dialpad', label: 'Dialpad' },
  { value: 'dialpad_websocket', label: 'Dialpad Websocket' },
  { value: 'call_tracking_metrics', label: 'Call Tracking Metrics' },
  { value: 'mightycall', label: 'Mighty Call' },
  { value: 'goto', label: 'Goto' },
  {
    value: 'atscall',
    label: 'Atscall',
    createLabel: 'Atscall (Voice For Pest, Voice for Pros, etc)',
  },
  { value: 'vonage_contact_center', label: 'Vonage Contact Center' },
  { value: 'broadworks_xsi', label: 'Broadworks XSI' },
  { value: 'intermedia_unite', label: 'Intermedia Unite' },
  // Present on existing records, never creatable from this page.
  { value: 'slack', label: 'Slack', creatable: false },
  { value: 'vonage_enterprise', label: 'Vonage Enterprise (Nexmo)', creatable: false },
]

export const creatableConnectorTypeOptions = CONNECTOR_TYPES.filter(
  (connector) => connector.creatable !== false
).map((connector) => ({
  value: connector.value,
  label: connector.createLabel || connector.label,
}))

export const getConnectorDisplayType = (type) =>
  CONNECTOR_TYPES.find((connector) => connector.value === type)?.label

/**
 * Drop the schema entries that exist only to render the form.
 *
 * AbstractForm seeds an initial value for EVERY schema entry, including `isStatic`
 * section headings and read-only panels, and IntegrationsSettings PUTs `values`
 * wholesale. Without this the headings are sent to the backend, and -- more visibly
 * -- diffIntegrations compares them against a record that has no such keys, so an
 * untouched form reports them as changes and Save pops the "these fields changed"
 * modal listing heading names.
 *
 * Done here rather than by changing what AbstractForm seeds: four other call sites
 * (CallSearchPage, DynamicInsightsPage, AnalyticsPage, CustomizableDashboardPage)
 * reference heading names as ordering keys, so narrowing the reducer is a separate,
 * riskier change.
 */
export const stripDisplayOnlyFields = (values, schema = []) => {
  const displayOnly = schema
    .filter((field) => field.isStatic || field.displayOnly)
    .map((field) => field.name)
  return omit(values, displayOnly)
}
